import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ContractPanel } from '../components/ContractPanel';
import { Icon } from '../components/Icon';
import { ShareModal } from '../components/ShareModal';
import { InvoiceEditor } from '../components/InvoiceEditor';
import { Attachments } from '../components/Attachments';
import { PdfViewer } from '../components/PdfViewer';
import { api, ApiError, files } from '../lib/api';
import { docStatusTone, TRACE_HINTS, TRACE_LABELS } from '../lib/docs';
import { amount, date, money } from '../lib/format';
import '../styles/documents.css';

interface Line {
  id: string;
  description: string;
  quantity: number;
  unit: string | null;
  unitPriceMinor: number;
  discountPct: number;
  taxPct: number | null;
  taxKind: string | null;
  lineTotalMinor: number;
}
interface TraceLink {
  type: string;
  present: boolean;
  id?: string;
  number?: string;
  status?: string;
  issueDate?: number | null;
  totalMinor?: number;
}
interface Version {
  id: string;
  version: number;
  level: number;
  levelLabel: string | null;
  reasonLabel: string | null;
  reasonNote: string | null;
  financialImpactMinor: number;
  author: string | null;
  createdAt: string | null;
}
interface TrailEvent {
  id: string;
  action: string;
  summary: string | null;
  actor: string | null;
  createdAt: string | null;
}
interface Allocation {
  id: string;
  amountMinor: number;
  document: { id: string; type: string; number: string; totalMinor: number } | null;
}
interface DocDetail {
  id: string;
  type: string;
  typeLabel: string;
  number: string;
  status: string;
  statusLabel: string;
  title: string | null;
  currency: string;
  subtotalMinor: number;
  discountMinor: number;
  taxMinor: number;
  totalMinor: number;
  paidMinor: number;
  dueMinor: number;
  issueDate: string | null;
  dueDate: string | null;
  validUntil: string | null;
  periodLabel: string | null;
  editMode: 'edit' | 'correct' | 'locked';
  sealed: boolean;
  lines: Line[];
  counterparty: {
    id: string;
    name: string;
    country: string | null;
    kycStatus: string | null;
    riskLevel: string | null;
  } | null;
  transitions: Array<{ status: string; label: string }>;
  conversions: Array<{ to: string; label: string; inherits: string[] }>;
  trace: TraceLink[];
  contractId?: string | null;
  versions: Version[];
  trail: TrailEvent[];
  allocations: Allocation[];
}

interface ReasonsResponse {
  reasons: Array<{ code: string; label: string }>;
}

/* ───────────────────────── Modal de corrección ───────────────────────── */

function CorrectionModal({
  doc,
  onClose,
  onDone,
}: {
  doc: DocDetail;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reasons, setReasons] = useState<ReasonsResponse['reasons']>([]);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [title, setTitle] = useState(doc.title ?? '');
  const [issueDate, setIssueDate] = useState(doc.issueDate ? doc.issueDate.slice(0, 10) : '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.get<ReasonsResponse>('/corrections/reasons').then((r) => {
      setReasons(r.reasons);
      setReason(r.reasons[0]?.code ?? '');
    }).catch(() => void 0);
  }, []);

  const submit = async () => {
    if (!reason || note.trim().length < 3) {
      setErr('Indica el motivo y una nota de al menos 3 caracteres.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.post(`/documents/${doc.id}/correct`, {
        reason,
        reasonNote: note.trim(),
        title: title !== (doc.title ?? '') ? title : undefined,
        issueDate: issueDate && issueDate !== (doc.issueDate?.slice(0, 10) ?? '') ? issueDate : undefined,
      });
      onDone();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not record the correction.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-scrim" onMouseDown={onClose}>
      <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal__head">
          <div>
            <span className="eyebrow">Correction</span>
            <h2 className="h2" style={{ marginTop: 4 }}>Corregir {doc.number}</h2>
          </div>
          <button className="icon-btn" onClick={onClose}><Icon name="x" size={18} /></button>
        </div>

        <div className="modal__body">
          <p className="muted" style={{ fontSize: 13, marginBottom: 16 }}>
            The document is issued, so it is not edited: it is corrected. The level (informative,
            tax or financial) is inferred from what you change, and the version is recorded
            anterior con su motivo.
          </p>

          <div className="field">
            <label>Motivo</label>
            <select className="select" value={reason} onChange={(e) => setReason(e.target.value)}>
              {reasons.map((r) => (
                <option key={r.code} value={r.code}>{r.label}</option>
              ))}
            </select>
          </div>

          <div className="field">
            <label>Nota explicativa</label>
            <textarea
              className="textarea"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Briefly explain the reason for the change…"
              rows={2}
            />
          </div>

          <div className="field">
            <label>Title / item</label>
            <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>

          <div className="field">
            <label>Issue date</label>
            <input className="input" type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
          </div>

          {err && <div className="act-error"><Icon name="alert" size={16} />{err}</div>}
        </div>

        <div className="modal__foot">
          <button className="btn btn--ghost" onClick={onClose}>Cancelar</button>
          <button className="btn btn--primary" onClick={submit} disabled={busy}>
            {busy ? 'Registrando…' : 'Record correction'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── Cadena de trazabilidad ────────────────────── */

function TraceChain({ trace, currentId, onGo }: { trace: TraceLink[]; currentId: string; onGo: (id: string) => void }) {
  return (
    <div className="trace">
      {trace.map((link, i) => (
        <div className="trace__link" key={link.type}>
          <button
            className={`trace__node ${link.present ? 'trace__node--present' : 'trace__node--absent'}`}
            disabled={!link.present || link.id === currentId}
            onClick={() => link.id && onGo(link.id)}
            style={link.id === currentId ? { boxShadow: 'inset 0 0 0 2px var(--accent)' } : undefined}
          >
            <div className="trace__stage">{TRACE_LABELS[link.type] ?? link.type}</div>
            {link.present ? (
              <div className="trace__num">{link.number}</div>
            ) : (
              <>
                <div className="trace__num trace__num--absent">—</div>
                <div className="trace__hint">{TRACE_HINTS[link.type]}</div>
              </>
            )}
          </button>
          {i < trace.length - 1 && <Icon name="arrow-right" size={16} className="trace__arrow" />}
        </div>
      ))}
    </div>
  );
}

/* ───────────────────────────── Pantalla ──────────────────────────────── */

export function DocumentDetail() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [doc, setDoc] = useState<DocDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [convOpen, setConvOpen] = useState(false);
  const [correcting, setCorrecting] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [viewingPdf, setViewingPdf] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(false);
  const [invoiceLines, setInvoiceLines] = useState<Array<{ description: string; quantity: number; unitPriceMinor: number; taxPct: number }>>([]);
  const [traceView, setTraceView] = useState<'chain' | 'list'>('chain');
  const [contracts, setContracts] = useState<Array<{ id: string; number: string; counterparty: string | null }>>([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const data = await api.get<DocDetail>(`/documents/${id}`);
      setDoc(data);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Document not found.');
    }
  }, [id]);

  useEffect(() => {
    setDoc(null);
    setErr(null);
    setActionErr(null);
    void load();
  }, [load]);

  // Abre el editor de factura cargando las líneas actuales del documento.
  const openInvoiceEditor = async () => {
    try {
      const full = await api.get<{ lines?: Array<{ description: string; quantity: number; unitPriceMinor: number; taxPct: number }> }>(`/documents/${id}`);
      setInvoiceLines(full.lines ?? []);
      setEditingInvoice(true);
    } catch {
      setInvoiceLines([]);
      setEditingInvoice(true);
    }
  };

  // Abre el PDF en una pestaña nueva. Método fiable: pide el PDF con la
  // credencial, crea un blob y lo abre. Evita el visor incrustado que algunos
  // navegadores bloquean. Abre la pestaña primero (gesto del usuario) para que
  // no la bloquee el bloqueador de pop-ups.
  const openPdfInNewTab = async () => {
    const tab = window.open('', '_blank');
    try {
      const url = await files.objectUrl(`/documents/${id}/pdf`);
      if (tab) { tab.location.href = url; }
      else { window.open(url, '_blank'); }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (tab) tab.close();
      setActionErr(e instanceof ApiError ? e.message : 'No se pudo abrir el PDF.');
    }
  };

  // Descarga el historial de cambios del documento como CSV.
  const downloadChangelog = async () => {
    try {
      const cl = await api.get<{ entries: Array<{ date: string; author: string; action: string; summary: string }> }>(`/documents/${id}/changelog`);
      const head = 'Fecha,Autor,Acción,Detalle\n';
      const esc = (s: string) => `"${(s ?? '').replace(/"/g, '""')}"`;
      const rows = cl.entries.map((e) => [new Date(e.date).toLocaleString('es-ES'), e.author, e.action, e.summary].map(esc).join(',')).join('\n');
      const blob = new Blob([head + rows], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `historial-${doc?.number ?? 'documento'}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      setActionErr(e instanceof ApiError ? e.message : 'No se pudo descargar el historial.');
    }
  };

  // Contratos a los que se puede imputar este documento.
  useEffect(() => {
    api.get<Array<{ id: string; number: string; counterparty: string | null }>>('/accounting/contracts')
      .then(setContracts)
      .catch(() => void 0);
  }, []);

  const doTransition = async (to: string) => {
    if (!doc) return;
    setBusy(true);
    setActionErr(null);
    try {
      await api.post(`/documents/${doc.id}/transition`, { to });
      await load();
    } catch (e) {
      setActionErr(e instanceof ApiError ? e.message : 'No se pudo cambiar el estado.');
    } finally {
      setBusy(false);
    }
  };

  const doConvert = async (to: string) => {
    if (!doc) return;
    setConvOpen(false);
    setBusy(true);
    setActionErr(null);
    try {
      const created = await api.post<{ id: string }>(`/documents/${doc.id}/convert`, { to });
      nav(`/documentos/${created.id}`);
    } catch (e) {
      setActionErr(e instanceof ApiError ? e.message : 'No se pudo convertir el documento.');
    } finally {
      setBusy(false);
    }
  };

  const doVoid = async () => {
    if (!doc) return;
    setBusy(true);
    setActionErr(null);
    try {
      await api.post(`/documents/${doc.id}/void`, {});
      await load();
    } catch (e) {
      setActionErr(e instanceof ApiError ? e.message : 'No se pudo anular el documento.');
    } finally {
      setBusy(false);
    }
  };

  if (err) {
    return (
      <main className="main">
        <button className="doc-back" onClick={() => nav('/documentos')}>
          <Icon name="arrow-right" size={15} style={{ transform: 'rotate(180deg)' }} /> Expedientes
        </button>
        <div className="card"><div className="empty">{err}</div></div>
      </main>
    );
  }
  if (!doc) return <main className="main"><div className="faint" style={{ padding: 40 }}>Cargando expediente…</div></main>;

  return (
    <main className="main">
      <button className="doc-back" onClick={() => nav(-1)}>
        <Icon name="arrow-right" size={15} style={{ transform: 'rotate(180deg)' }} /> Volver
      </button>

      <div className="doc-head">
        <div>
          <span className="eyebrow">{doc.typeLabel}</span>
          <div className="doc-head__id" style={{ marginTop: 6 }}>
            <span className="doc-head__num">{doc.number}</span>
            <span className={`pill pill--${docStatusTone(doc.status)}`}>
              <span className="pill__dot" />
              {doc.statusLabel}
            </span>
          </div>
          {doc.title && <p className="muted" style={{ marginTop: 8, fontSize: 14 }}>{doc.title}</p>}
        </div>
        <div className="doc-head__amt">
          <div className="doc-head__total">{money(doc.totalMinor, doc.currency)}</div>
          {doc.dueMinor > 0 && doc.paidMinor > 0 && (
            <div className="doc-head__due">Pendiente {money(doc.dueMinor, doc.currency)}</div>
          )}
        </div>
      </div>

      {/* Acciones */}
      <div className="doc-actions">
        {doc.transitions.map((t) => (
          <button key={t.status} className="btn btn--ghost" disabled={busy} onClick={() => doTransition(t.status)}>
            {t.label}
          </button>
        ))}

        {doc.conversions.length > 0 && (
          <div style={{ position: 'relative' }}>
            <button className="btn btn--ghost" disabled={busy} onClick={() => setConvOpen((v) => !v)}>
              <Icon name="layers" size={15} /> Convertir
              <Icon name="chevron-down" size={13} />
            </button>
            {convOpen && (
              <div className="conv-menu">
                {doc.conversions.map((c) => (
                  <button key={c.to} className="conv-menu__item" onClick={() => doConvert(c.to)}>
                    <div className="conv-menu__label">{c.label}</div>
                    {c.inherits.length > 0 && (
                      <div className="conv-menu__inherits">hereda {c.inherits.join(', ')}</div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="doc-actions__spacer" />

        <button className="btn btn--ghost" onClick={openPdfInNewTab}>
          <Icon name="file" size={15} /> Ver PDF
        </button>

        <button className="btn btn--accent" onClick={() => setSharing(true)}>
          <Icon name="share" size={15} /> Compartir
        </button>

        {['invoice', 'vendor_invoice', 'credit_note', 'receipt'].includes(doc.type) && (
          <button className="btn btn--ghost" onClick={openInvoiceEditor}>
            <Icon name="sliders" size={15} /> Editar factura
          </button>
        )}

        <button className="btn btn--ghost" onClick={downloadChangelog}>
          <Icon name="download" size={15} /> Historial
        </button>

        {doc.editMode === 'correct' && (
          <button className="btn btn--ghost" disabled={busy} onClick={() => setCorrecting(true)}>
            <Icon name="sliders" size={15} /> Corregir
          </button>
        )}
        {doc.status !== 'void' && !doc.sealed && (
          <button className="btn btn--ghost" disabled={busy} onClick={doVoid} style={{ color: 'var(--clay)' }}>
            Anular
          </button>
        )}
      </div>

      {actionErr && <div className="act-error"><Icon name="alert" size={16} />{actionErr}</div>}

      {/* Documentos adjuntos (PDF originales del histórico) */}
      <div className="card" style={{ marginBottom: 4 }}>
        <Attachments docId={doc.id} />
      </div>

      {/* Cadena de trazabilidad */}
      <div className="card" style={{ marginBottom: 4 }}>
        <div className="card__head">
          <span className="card__title">Trazabilidad · necesidad → recibo</span>
          <div className="trace-tabs">
            <button className={traceView === 'chain' ? 'is-on' : ''} onClick={() => setTraceView('chain')}>Cadena</button>
            <button className={traceView === 'list' ? 'is-on' : ''} onClick={() => setTraceView('list')}>Listado completo</button>
          </div>
        </div>
        <div className="card__body">
          {traceView === 'chain' ? (
            <TraceChain trace={doc.trace} currentId={doc.id} onGo={(x) => nav(`/documentos/${x}`)} />
          ) : (
            <table className="tlist">
              <thead>
                <tr><th>Etapa</th><th>Documento</th><th>Estado</th><th>Fecha</th><th className="r">Importe</th></tr>
              </thead>
              <tbody>
                {doc.trace.map((l) => (
                  <tr
                    key={l.type}
                    className={l.id === doc.id ? 'is-current' : ''}
                    onClick={() => l.id && nav(`/documentos/${l.id}`)}
                    style={{ cursor: l.id ? 'pointer' : 'default', opacity: l.present ? 1 : 0.5 }}
                  >
                    <td className="tlist__stage">{TRACE_LABELS[l.type] ?? l.type}</td>
                    <td className="num">{l.number ?? <span className="faint">{TRACE_HINTS[l.type] ?? 'Sin emitir'}</span>}</td>
                    <td>{l.status ?? '—'}</td>
                    <td className="num">{l.issueDate ? new Date(l.issueDate).toLocaleDateString('es-ES') : '—'}</td>
                    <td className="r num">{l.totalMinor != null ? money(l.totalMinor, doc.currency) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Imputación contable */}
          <div className="alloc-bar">
            <span className="eyebrow" style={{ marginBottom: 0 }}>Imputado a</span>
            <select
              className="select select--sm"
              style={{ minWidth: 230 }}
              value={doc.contractId ?? ''}
              onChange={async (e) => {
                const v = e.target.value || null;
                try { await api.patch(`/documents/${doc.id}/allocation`, { contractId: v }); void load(); }
                catch (err) { setActionErr(err instanceof ApiError ? err.message : 'No se pudo imputar.'); }
              }}
            >
              <option value="">Gastos generales de la empresa</option>
              {contracts.map((c) => <option key={c.id} value={c.id}>{c.number}{c.counterparty ? ` · ${c.counterparty}` : ''}</option>)}
            </select>
            <span className="faint" style={{ fontSize: 12 }}>Determines which contract or project this document counts toward in Accounting.</span>
          </div>
        </div>
      </div>

      {(doc.type === 'contract' || doc.type === 'quote') && <ContractPanel docId={doc.id} docType={doc.type} docNumber={doc.number} />}

      <div className="doc-grid">
        {/* Izquierda: líneas + totales + conciliación */}
        <div className="col gap-20">
          <div className="card">
            <div className="card__head"><span className="card__title">Detalle</span></div>
            <div className="card__body card__body--flush" style={{ padding: '4px 8px' }}>
              <table className="lines">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th className="r">Cant.</th>
                    <th className="r">Precio</th>
                    <th className="r">Importe</th>
                  </tr>
                </thead>
                <tbody>
                  {doc.lines.map((l) => (
                    <tr key={l.id}>
                      <td>
                        <div className="lines__desc">{l.description}</div>
                        {l.taxKind && l.taxKind !== 'standard' && (
                          <div className="lines__sub">{l.taxKind.replace('_', ' ')}</div>
                        )}
                      </td>
                      <td className="r num">{l.quantity}{l.unit ? ` ${l.unit}` : ''}</td>
                      <td className="r num">{amount(l.unitPriceMinor, doc.currency)}</td>
                      <td className="r num">{amount(l.lineTotalMinor, doc.currency)}</td>
                    </tr>
                  ))}
                  {doc.lines.length === 0 && (
                    <tr><td colSpan={4}><div className="empty" style={{ padding: 18 }}>No detail lines.</div></td></tr>
                  )}
                </tbody>
              </table>

              <div className="totals">
                <div className="totals__row"><span className="muted">Subtotal</span><span className="num">{amount(doc.subtotalMinor, doc.currency)}</span></div>
                {doc.discountMinor > 0 && (
                  <div className="totals__row"><span className="muted">Descuento</span><span className="num">−{amount(doc.discountMinor, doc.currency)}</span></div>
                )}
                <div className="totals__row"><span className="muted">Impuestos</span><span className="num">{amount(doc.taxMinor, doc.currency)}</span></div>
                <div className="totals__row totals__grand"><span>Total</span><span className="num">{amount(doc.totalMinor, doc.currency)} {doc.currency}</span></div>
                {doc.paidMinor > 0 && (
                  <div className="totals__row"><span className="muted">Cobrado</span><span className="num" style={{ color: 'var(--accent)' }}>{amount(doc.paidMinor, doc.currency)}</span></div>
                )}
              </div>
            </div>
          </div>

          {doc.allocations.length > 0 && (
            <div className="card">
              <div className="card__head"><span className="card__title">Reconciliation</span></div>
              <div className="card__body card__body--flush">
                {doc.allocations.map((a) => (
                  <div key={a.id} className="alloc">
                    <button className="alloc__doc" onClick={() => a.document && nav(`/documentos/${a.document.id}`)}>
                      {a.document?.number ?? '—'}
                    </button>
                    <span className="alloc__amt">{money(a.amountMinor, doc.currency)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Derecha: ficha + versiones + auditoría */}
        <div className="col gap-20">
          <div className="card">
            <div className="card__head"><span className="card__title">Ficha</span></div>
            <div className="card__body" style={{ paddingTop: 6, paddingBottom: 6 }}>
              {doc.counterparty && (
                <div className="meta__row">
                  <span className="meta__k">Contraparte</span>
                  <span className="meta__v">{doc.counterparty.name}</span>
                </div>
              )}
              {doc.counterparty?.kycStatus && (
                <div className="meta__row">
                  <span className="meta__k">KYC</span>
                  <span className="meta__v">{doc.counterparty.kycStatus}</span>
                </div>
              )}
              <div className="meta__row"><span className="meta__k">Issuance</span><span className="meta__v num">{date(doc.issueDate)}</span></div>
              {doc.dueDate && <div className="meta__row"><span className="meta__k">Vencimiento</span><span className="meta__v num">{date(doc.dueDate)}</span></div>}
              {doc.validUntil && <div className="meta__row"><span className="meta__k">Valid until</span><span className="meta__v num">{date(doc.validUntil)}</span></div>}
              {doc.periodLabel && <div className="meta__row"><span className="meta__k">Periodo</span><span className="meta__v num">{doc.periodLabel}</span></div>}
              <div className="meta__row"><span className="meta__k">Moneda</span><span className="meta__v num">{doc.currency}</span></div>
            </div>
          </div>

          {doc.versions.length > 0 && (
            <div className="card">
              <div className="card__head"><span className="card__title">Correcciones</span></div>
              <div className="card__body">
                <div className="timeline">
                  {doc.versions.slice().reverse().map((v) => (
                    <div key={v.id} className="timeline__item timeline__item--accent">
                      <div className="timeline__head">
                        <span className="timeline__title">{v.levelLabel ?? `Nivel ${v.level}`} · {v.reasonLabel ?? '—'}</span>
                        <span className="timeline__date">{date(v.createdAt)}</span>
                      </div>
                      {v.reasonNote && <div className="timeline__body">{v.reasonNote}</div>}
                      {v.financialImpactMinor !== 0 && (
                        <div className={`timeline__impact ${v.financialImpactMinor > 0 ? 'timeline__impact--up' : 'timeline__impact--down'}`}>
                          Impacto {v.financialImpactMinor > 0 ? '+' : ''}{amount(v.financialImpactMinor, doc.currency)} {doc.currency}
                        </div>
                      )}
                      {v.author && <div className="timeline__body faint">por {v.author}</div>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="card">
            <div className="card__head"><span className="card__title">Audit</span></div>
            <div className="card__body">
              {doc.trail.length === 0 ? (
                <div className="empty" style={{ padding: 8 }}>Sin eventos.</div>
              ) : (
                <div className="timeline">
                  {doc.trail.slice().reverse().map((t) => (
                    <div key={t.id} className="timeline__item">
                      <div className="timeline__head">
                        <span className="timeline__title">{t.summary ?? t.action}</span>
                        <span className="timeline__date">{date(t.createdAt)}</span>
                      </div>
                      {t.actor && <div className="timeline__body faint">{t.actor}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {correcting && (
        <CorrectionModal
          doc={doc}
          onClose={() => setCorrecting(false)}
          onDone={() => {
            setCorrecting(false);
            void load();
          }}
        />
      )}

      {viewingPdf && (
        <PdfViewer
          title={`${doc.typeLabel} ${doc.number}`}
          apiPath={`/documents/${doc.id}/pdf`}
          filename={`${doc.number}.pdf`}
          previewPath={`/documents/${doc.id}/share`}
          onClose={() => setViewingPdf(false)}
        />
      )}

      {editingInvoice && doc && (
        <InvoiceEditor
          docId={doc.id}
          docNumber={doc.number}
          currency={doc.currency}
          initialLines={invoiceLines}
          onClose={() => setEditingInvoice(false)}
          onSaved={() => { setEditingInvoice(false); void load(); }}
        />
      )}

      {sharing && <ShareModal docId={doc.id} docNumber={doc.number} docTypeLabel={doc.typeLabel} onClose={() => setSharing(false)} />}
    </main>
  );
}
